/**
 * Choosing which drum the selected notes are.
 *
 * Typing a letter on a percussion staff writes the drum nearest that line, which is enough
 * for the kick, the snare and the toms because each of them has a line to itself. It is not
 * enough for the cymbals: the closed and the open hi-hat are written on the same place and
 * told apart by their heads, so something has to be able to say which of the two a note is.
 * That is what this layer is for -- the drum is named, and the head moves to wherever the
 * kit writes it.
 *
 * Nothing here invents a kit. A part that is not percussion refuses, in words, rather than
 * quietly turning into one, because a staff that sounds a snare where a clarinet was
 * written is worse than an edit that did not happen.
 */

import { drumNamed, drumOf } from "../document/percussion";
import type { DrumNote, PercussionKit } from "../document/percussion";
import { drumHeadOf } from "../document/pitch";
import type { Command } from "../commands/types";
import type { PartId } from "../document/ids";
import type { Score } from "../document/types";
import { selectedNotes } from "./selection";
import type { Selection } from "./selection";

/** The kit a part is written on, or `null` when it is written in pitches. */
export function kitOf(score: Score, partId: PartId | null): PercussionKit | null {
  if (partId === null) return null;
  return score.parts.find((part) => part.id === partId)?.instrument.percussion ?? null;
}

/** The kit the selection is written on, or `null` when it spans none or several. */
export function kitInSelection(score: Score, selection: Selection): PercussionKit | null {
  const parts = new Set(selectedNotes(score, selection).map((at) => at.partId));
  if (parts.size !== 1) return null;
  return kitOf(score, [...parts][0] ?? null);
}

/** The drums the selected notes are, which is what a menu ticks. */
export function drumsInSelection(score: Score, selection: Selection): readonly string[] {
  const names = new Set<string>();
  for (const at of selectedNotes(score, selection)) {
    for (const pitch of at.event.pitches) if (pitch.drum !== null) names.add(pitch.drum);
  }
  return [...names];
}

/**
 * Make every selected note the named drum.
 *
 * A chord becomes one drum rather than keeping its other heads: two drums struck together
 * are written as a chord of two named drums, and there is no reading of "make this a
 * snare" that keeps the cymbal that was under it.
 */
export function setDrum(score: Score, selection: Selection, name: string): Command | string {
  const kit = kitInSelection(score, selection);
  if (kit === null) return "select notes on one percussion staff first";
  const drum: DrumNote | null = drumNamed(kit, name);
  if (drum === null) return `this kit has no ${name}`;

  const found = selectedNotes(score, selection);
  if (found.length === 0) return "no note is selected";
  const pitches = [drumHeadOf(drum.step, drum.octave, drum.name)];
  return {
    kind: "batch",
    label: drum.name.toLowerCase(),
    commands: found.map((at) => ({
      kind: "setPitches" as const,
      eventId: at.event.id,
      pitches,
    })),
  };
}

/** What the selected notes sound, for a panel that names them. */
export function soundsOf(score: Score, selection: Selection): readonly DrumNote[] {
  const kit = kitInSelection(score, selection);
  if (kit === null) return [];
  const drums: DrumNote[] = [];
  for (const name of drumsInSelection(score, selection)) {
    const drum = drumOf(kit, name);
    if (drum !== null) drums.push(drum);
  }
  return drums;
}
